import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "./Button";
import { Input } from "./Input";

export type LeadSystemSelection = {
  canonical_id: string | null;
  user_lead_system_id: number | null;
  snapshot: string;
};

export const emptyLeadSystem = (): LeadSystemSelection => ({
  canonical_id: null,
  user_lead_system_id: null,
  snapshot: "",
});

export function LeadSystemPicker({
  id,
  label = "Lead system",
  value,
  onChange,
  speciesFilter,
}: {
  id: string;
  label?: string;
  value: LeadSystemSelection;
  onChange: (value: LeadSystemSelection) => void;
  speciesFilter?: string;
}) {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState(value.snapshot || "");
  const [open, setOpen] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [privateOnly, setPrivateOnly] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const params = useMemo(() => {
    const p = new URLSearchParams({ q: query.trim() });
    if (speciesFilter) p.set("species", speciesFilter);
    return p.toString();
  }, [query, speciesFilter]);

  const knownQuery = useQuery({
    queryKey: ["lead_system_search", query, speciesFilter],
    queryFn: () => fetch(`/app-api/lead-systems/search?${params}`).then((r) => (r.ok ? r.json() : [])),
    enabled: open,
    staleTime: 30_000,
  });
  const mineQuery = useQuery({
    queryKey: ["lead_systems_mine"],
    queryFn: () => fetch("/app-api/lead-systems/mine").then((r) => (r.ok ? r.json() : [])),
  });

  const known: any[] = knownQuery.data ?? [];
  const mine: any[] = mineQuery.data ?? [];
  const mineMatches = mine.filter((row) =>
    !query.trim() || String(row.display_name ?? "").toLowerCase().includes(query.trim().toLowerCase()),
  );

  function chooseKnown(row: any) {
    const snapshot = String(row.canonical_name ?? "");
    onChange({ canonical_id: String(row.id), user_lead_system_id: null, snapshot });
    setQuery(snapshot);
    setOpen(false);
    setMessage("");
  }

  function chooseMine(row: any) {
    const snapshot = String(row.display_name ?? "");
    onChange({
      canonical_id: row.linked_canonical_lead_system_id ?? null,
      user_lead_system_id: Number(row.id),
      snapshot,
    });
    setQuery(snapshot);
    setOpen(false);
    setMessage("");
  }

  async function addNow() {
    const name = newName.trim();
    if (!name || saving) return;
    setSaving(true);
    setMessage("");
    try {
      const response = await fetch("/app-api/lead-systems/add", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          species_applicability: speciesFilter || "both",
          private_only: privateOnly,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.id) throw new Error(data.error || "Could not add lead system");
      onChange({ canonical_id: null, user_lead_system_id: Number(data.id), snapshot: data.display_name || name });
      setQuery(data.display_name || name);
      setShowAdd(false);
      setOpen(false);
      setNewName("");
      await queryClient.invalidateQueries({ queryKey: ["lead_systems_mine"] });
      setMessage(privateOnly ? "Private lead system added and selected." : "Added and selected. CatchCompass Admin will review it for the shared list.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not add lead system");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="relative">
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-primary">{label}</label>
      <div className="flex gap-2">
        <Input
          id={id}
          value={query}
          onChange={(event) => {
            const next = event.target.value;
            setQuery(next);
            onChange({ canonical_id: null, user_lead_system_id: null, snapshot: next });
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 180)}
          placeholder="Helicopter, lead clip, inline…"
          autoComplete="off"
        />
        {value.snapshot ? (
          <button
            type="button"
            className="shrink-0 px-2 text-xs text-secondary"
            aria-label="Clear lead system"
            onClick={() => {
              setQuery("");
              onChange(emptyLeadSystem());
            }}
          >
            ✕
          </button>
        ) : null}
      </div>

      {open ? (
        <div className="absolute left-0 right-0 z-50 mt-1 max-h-72 overflow-y-auto rounded-lg border border-border bg-raised shadow-xl">
          {known.map((row) => (
            <button key={row.id} type="button" onMouseDown={() => chooseKnown(row)} className="block w-full px-3 py-2.5 text-left hover:bg-inset">
              <span className="block text-sm font-medium text-primary">{row.canonical_name}</span>
              <span className="text-xs text-secondary">Known lead system</span>
            </button>
          ))}
          {mineMatches.length ? <p className="border-t border-border px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-secondary">Your added systems</p> : null}
          {mineMatches.map((row) => (
            <button key={`mine-${row.id}`} type="button" onMouseDown={() => chooseMine(row)} className="block w-full px-3 py-2.5 text-left hover:bg-inset">
              <span className="block text-sm font-medium text-primary">{row.display_name}</span>
              <span className="text-xs text-secondary">Personal</span>
            </button>
          ))}
          <button
            type="button"
            onMouseDown={() => {
              setNewName(query.trim());
              setShowAdd(true);
              setOpen(false);
            }}
            className="w-full border-t border-border px-3 py-2.5 text-left text-xs font-semibold text-accent"
          >
            + Add lead system not listed
          </button>
        </div>
      ) : null}

      {showAdd ? (
        <div className="mt-2 space-y-3 rounded-lg border border-accent/40 bg-raised p-3">
          <p className="text-sm font-semibold text-primary">Add lead system</p>
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Lead system name" />
          <label className="flex items-center gap-2 text-xs text-secondary">
            <input type="checkbox" checked={privateOnly} onChange={(e) => setPrivateOnly(e.target.checked)} />
            Keep this private rather than sending it for Admin review
          </label>
          <div className="flex gap-2">
            <Button type="button" size="sm" onClick={addNow} isLoading={saving}>Add and use now</Button>
            <Button type="button" size="sm" variant="tertiary" onClick={() => setShowAdd(false)}>Cancel</Button>
          </div>
        </div>
      ) : null}
      {message ? <p className="mt-1 text-xs text-secondary" role="status">{message}</p> : null}
    </div>
  );
}
